import type { ComponentType } from "react";
import { ListTodo, MessageSquare } from "lucide-react";
import { SourceId } from "@workspace/api-client-react";
import { GmailLogo, OutlookLogo, SlackLogo, TeamsLogo, JiraLogo } from "@/components/BrandMarks";

// The single registry every provider-facing UI reads from instead of
// branching on a source string directly (see docs/standards/09-integrations.md,
// INT-11). Keyed as Record<SourceId, ...> so adding a value to the generated
// SourceId enum (lib/api-spec/openapi.yaml) makes TypeScript flag this file
// as missing an entry — real exhaustiveness instead of a prose comment
// listing which sources exist.
export type { SourceId };

export type SourceCategory = "email" | "chat" | "task";

// Deliberately just the flags needed by today's shared components
// (MessageRow's context menu, FolderNav, ComposeModal) — not a speculative
// full capability model. Add a flag when a second consumer needs it.
export interface SourceCapabilities {
  canReply: boolean;
  canArchive: boolean;
  canSnooze: boolean;
  canPin: boolean;
  canForward: boolean;
}

export interface SourceDescriptor {
  id: SourceId;
  category: SourceCategory;
  label: string;
  icon: ComponentType<{ className?: string }>;
  // False only for "native" — first-party chat channels have no OAuth
  // connection, reconnect flow, or token to expire, so UI that shows
  // connection/reconnect state should skip sources where this is false.
  isThirdParty: boolean;
  capabilities: SourceCapabilities;
}

const emailCapabilities: SourceCapabilities = {
  canReply: true,
  canArchive: true,
  canSnooze: true,
  canPin: true,
  canForward: true,
};

const chatCapabilities: SourceCapabilities = {
  canReply: true,
  canArchive: true,
  canSnooze: false,
  canPin: true,
  canForward: false,
};

const taskCapabilities: SourceCapabilities = {
  canReply: false,
  canArchive: false,
  canSnooze: false,
  canPin: false,
  canForward: false,
};

export const SOURCE_REGISTRY: Record<SourceId, SourceDescriptor> = {
  [SourceId.gmail]: {
    id: SourceId.gmail,
    category: "email",
    label: "Gmail",
    icon: GmailLogo,
    isThirdParty: true,
    capabilities: emailCapabilities,
  },
  [SourceId.outlook]: {
    id: SourceId.outlook,
    category: "email",
    label: "Outlook",
    icon: OutlookLogo,
    isThirdParty: true,
    capabilities: emailCapabilities,
  },
  [SourceId.slack]: {
    id: SourceId.slack,
    category: "chat",
    label: "Slack",
    icon: SlackLogo,
    isThirdParty: true,
    capabilities: chatCapabilities,
  },
  [SourceId.teams]: {
    id: SourceId.teams,
    category: "chat",
    label: "Teams",
    icon: TeamsLogo,
    isThirdParty: true,
    capabilities: chatCapabilities,
  },
  [SourceId.jira]: {
    id: SourceId.jira,
    category: "task",
    label: "Jira",
    icon: JiraLogo,
    isThirdParty: true,
    capabilities: taskCapabilities,
  },
  [SourceId.google_tasks]: {
    id: SourceId.google_tasks,
    category: "task",
    label: "Google Tasks",
    icon: ListTodo,
    isThirdParty: true,
    capabilities: taskCapabilities,
  },
  [SourceId.native]: {
    id: SourceId.native,
    category: "chat",
    label: "Channels",
    icon: MessageSquare,
    isThirdParty: false,
    capabilities: chatCapabilities,
  },
};

export function sourceDescriptor(id: SourceId): SourceDescriptor {
  return SOURCE_REGISTRY[id];
}
